import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import '../styles/Login.css';
import { Icon } from '../components/ui';
import { ChangePasswordModal } from '../components/ChangePasswordModal';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { OrbitingCircles } from '../components/magicui/orbiting-circles';
import {
  LockClosedIcon,
  CpuChipIcon,
  BoltIcon,
  KeyIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import fidarDark from '../images/Agenti5_dark_bck.png';
import fidarLight from '../images/Agenti5_light_bck.png';
import { useTranslation } from 'react-i18next';

interface LoginProps {
  onLogin: () => Promise<void> | void;
  useKeycloak: boolean;
  useSaml?: boolean;
  isDarkMode: boolean;
  onToggleDarkMode: () => void;
}

const Login: React.FC<LoginProps> = ({ onLogin, useKeycloak, useSaml, isDarkMode, onToggleDarkMode }) => {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [localAuthEnabled, setLocalAuthEnabled] = useState(false);
  const [forceChange, setForceChange] = useState(false); // show forced password-change modal
  const navigate = useNavigate();

  // Resolve the firewall API base (runtime-injected wins so the POST hits the
  // firewall's public hostname, not a non-routed :3000 on the dashboard host).
  const getApiUrl = () => {
    const runtimeEnv = (window as any).__ZTAI_ENV__ || {};
    if (runtimeEnv.API_PORT) return `${window.location.protocol}//${window.location.hostname}:${runtimeEnv.API_PORT}`;
    return runtimeEnv.API_URL || process.env.REACT_APP_API_URL || `${window.location.protocol}//${window.location.hostname}:3000`;
  };

  // Detect whether ZTAI-native local password auth is enabled for this instance.
  useEffect(() => {
    let active = true;
    fetch(`${getApiUrl()}/auth/local/config`)
      .then((r) => (r.ok ? r.json() : { enabled: false }))
      .then((cfg) => { if (active) setLocalAuthEnabled(!!cfg.enabled); })
      .catch(() => { /* config unavailable — fall back to mock/SSO */ });
    return () => { active = false; };
  }, []);

  // Complete login once a valid token is stored (shared by local + mock paths).
  const finishLogin = async () => {
    await onLogin();
    navigate('/dashboard');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (localAuthEnabled) {
        // ── ZTAI-native local password authentication ──────────────────────
        const resp = await fetch(`${getApiUrl()}/auth/local/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username, password }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          setError(data.error || 'Invalid username or password.');
          setPassword('');
          setIsLoading(false);
          return;
        }
        localStorage.setItem('ztai-saml-token', data.token);
        if (data.mustChangePassword) {
          // Force a password change before entering the app.
          setForceChange(true);
          setIsLoading(false);
          return;
        }
        await finishLogin();
      } else if (username.trim().toLowerCase() === 'admin' && password === '12345') {
        // ── Mock login (dev/demo without local auth or SSO) ────────────────
        try {
          const resp = await fetch(`${getApiUrl()}/auth/mock/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, password }),
          });
          if (resp.ok) {
            const { token } = await resp.json();
            localStorage.setItem('ztai-saml-token', token);
          }
        } catch (e) {
          console.warn('[Mock login] Could not fetch JWT from firewall:', e);
        }
        await finishLogin();
      } else if (useKeycloak || useSaml) {
        // For non-mock credentials, redirect to SSO
        await onLogin();
      } else {
        setError('Invalid username or password.');
        setPassword('');
      }
    } catch (loginError: any) {
      setError(loginError?.message || 'Login failed. Please try again.');
    }

    setIsLoading(false);
  };

  return (
    <div className={`lp-login-root${isDarkMode ? '' : ' lp-login-light'}`}>
      <div className="lp-login-lang">
        <LanguageSwitcher />
      </div>
      {/* Forced password change on first login / after admin reset */}
      {forceChange && (
        <ChangePasswordModal
          apiUrl={getApiUrl()}
          forced
          onClose={() => {}}
          onSuccess={() => { setForceChange(false); finishLogin(); }}
        />
      )}
      <button
        className="lp-login-theme-toggle"
        onClick={onToggleDarkMode}
        aria-label={isDarkMode ? t('pages.login.switchToLightMode') : t('pages.login.switchToDarkMode')}
      >
        <Icon name={isDarkMode ? 'sun' : 'moon'} size={18} />
      </button>

      <div className="lp-login-panel">
        <div className="lp-login-brand">
          <img
            src={isDarkMode ? fidarDark : fidarLight}
            alt={t('pages.login.fidar')}
            className="lp-login-logo"
          />
        </div>

        {/* Orbiting circles + centered text */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="lp-login-orbit-wrapper"
        >
          <div className="lp-login-orbit-container">
            {/* Ambient glow behind center */}
            <div className='absolute w-40 h-40 rounded-full blur-3xl opacity-10' style={{ backgroundColor: '#6e6589' }} />

            {/* Inner orbit */}
            <OrbitingCircles radius={130} duration={22} delay={0} className="size-11">
              <div className='lp-orbit-icon'>
                <LockClosedIcon className="w-5 h-5" />
              </div>
            </OrbitingCircles>
            <OrbitingCircles radius={130} duration={22} delay={7} className="size-11">
              <div className='lp-orbit-icon'>
                <KeyIcon className="w-5 h-5" />
              </div>
            </OrbitingCircles>
            <OrbitingCircles radius={130} duration={22} delay={14} className="size-11">
              <div className='lp-orbit-icon'>
                <CpuChipIcon className="w-5 h-5" />
              </div>
            </OrbitingCircles>

            {/* Outer orbit */}
            <OrbitingCircles radius={210} duration={32} delay={0} reverse className="size-11">
              <div className='lp-orbit-icon'>
                <BoltIcon className="w-5 h-5" />
              </div>
            </OrbitingCircles>
            <OrbitingCircles radius={210} duration={32} delay={16} reverse className="size-11">
              <div className='lp-orbit-icon'>
                <UserCircleIcon className="w-5 h-5" />
              </div>
            </OrbitingCircles>

            {/* Center text overlay */}
            <div className="lp-login-center-text">
              <h2>{t('pages.login.zeroTRUSTAI')}</h2>
              <h6 className="lp-login-subtitle">{t('pages.login.agenticSOCPlatform')}</h6>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="lp-login-form-panel">
        <button className="lp-login-back" onClick={() => navigate('/')}>
          <Icon name="chevronRight" size={19} />
          <span>{t('pages.login.backToHome')}</span>
        </button>
        <div className="lp-login-card">
          <div className="lp-login-card-header">
            <h1>{t('pages.login.welcomeBack')}</h1>
            <p>{(useKeycloak || useSaml) ? t('pages.login.continueWithYourOrganizationIdentityProvider') : t('pages.login.signInToAccessYourDashboard')}</p>
          </div>

          <form onSubmit={handleSubmit} className="lp-login-form">
            {error && (
              <div className="lp-login-error">
                <Icon name="warning" size={16} />
                {error}
              </div>
            )}

            {useKeycloak && (
              <div className="lp-field">
                <label>{t('pages.login.identityProvider')}</label>
                <div className="lp-input-wrap">
                  <Icon name="shield" size={16} />
                  <input value="Keycloak SSO" disabled />
                </div>
              </div>
            )}

            {useSaml && (
              <div className="lp-field">
                <label>{t('pages.login.identityProvider')}</label>
                <div className="lp-input-wrap">
                  <Icon name="shield" size={16} />
                  <input value="SAML SSO (PQC Token)" disabled />
                </div>
              </div>
            )}

            {(useKeycloak || useSaml) && (
              <button
                type="submit"
                className="lp-login-btn"
                disabled={isLoading}
              >
                {isLoading ? (
                  <><span className="lp-spinner" />{t('pages.login.signingIn')}</>
                ) : useKeycloak ? (
                  <>{t('pages.login.continueWithSSO')} <Icon name="chevronRight" size={16} /></>
                ) : (
                  <>{t('pages.login.continueWithSAML')} <Icon name="chevronRight" size={16} /></>
                )}
              </button>
            )}

            {!useKeycloak && !useSaml && (
              <>
                <div className="lp-field">
                  <label htmlFor="username">{t('pages.login.username')}</label>
                  <div className="lp-input-wrap">
                    <Icon name="users" size={16} />
                    <input
                      id="username"
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder={t('pages.login.enterYourUsername')}
                      disabled={isLoading}
                      autoFocus
                      autoComplete="username"
                    />
                  </div>
                </div>

                <div className="lp-field">
                  <label htmlFor="password">{t('pages.login.password')}</label>
                  <div className="lp-input-wrap">
                    <Icon name="lock" size={16} />
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t('pages.login.enterYourPassword')}
                      disabled={isLoading}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="lp-pw-toggle"
                      onClick={() => setShowPassword((v) => !v)}
                      tabIndex={-1}
                    >
                      <Icon name={showPassword ? 'view' : 'key'} size={15} />
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  className="lp-login-btn"
                  disabled={isLoading || !username || !password}
                >
                  {isLoading ? (
                    <><span className="lp-spinner" />{t('pages.login.signingIn')}</>
                  ) : (
                    <>{t('pages.login.signIn')} <Icon name="chevronRight" size={16} /></>
                  )}
                </button>
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
